import { useMemo, useState, useEffect, useCallback } from 'react';
import type { UseIssuesInput } from '@atlassian/jira-business-common/src/common/types/query-input.tsx';
import { getVariables } from '@atlassian/jira-business-common/src/common/utils/get-variables/index.tsx';
import { getGraphqlErrorAttributes } from '@atlassian/jira-business-error-handling/src/utils/partial-data/index.tsx';
import { useSorter } from '@atlassian/jira-business-filters/src/services/sorting/index.tsx';
import { useJqlPagination } from '@atlassian/jira-business-list-handlers/src/controllers/pagination/main.tsx';
import { getAGGCompliantData } from '@atlassian/jira-business-list-handlers/src/controllers/pagination/utils.tsx';
import { REQUEST_END, REQUEST_START } from '@atlassian/jira-business-performance/src/constants.tsx';
import { markViewMetric } from '@atlassian/jira-business-performance/src/ui/page-load/utils.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { shouldHandleTheError } from '../../common/utils/partial-data/index.tsx';
import type {
	JWMListViewItems,
	JWMListViewItemsVariables as ListVariables,
} from './__generated_apollo__/JWMListViewItems';
import type {
	ListViewItemEdges_edges as ListEdges,
	ListViewItemEdges_edges_node as ListNode,
} from './__generated_apollo__/ListViewItemEdges';
import { LIST_VIEW_QUERY } from './gql.tsx';
import type { ListViewIssue, ListViewIssuesReturn } from './types.tsx';
import { transformResult } from './utils.tsx';

const emptyArray: Array<never> = [];

// Loads the list and takes care of pagination
export const useListViewIssues = ({
	input,
	condition = true,
	onComplete,
}: {
	input: UseIssuesInput;
	condition?: boolean;
	onComplete?: (data: JWMListViewItems) => void;
}): ListViewIssuesReturn => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [hasLoadingStarted, setHasLoadingStarted] = useState(false);
	const cloudId = useCloudId();

	const onNextPageLoaded = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'table endReached');
	}, [createAnalyticsEvent]);

	const queryVariables = getVariables(input);

	const { data, loadFirstPage, hasNextPage, loading, error, loadNextPage, isLoadingNextPage } =
		useJqlPagination<JWMListViewItems, ListNode, ListVariables>({
			variables: {
				cloudId,
				...queryVariables,
			},
			errorPolicy: 'all',
			query: LIST_VIEW_QUERY,
			onCompleted: (responseData) => {
				const listData = getAGGCompliantData<JWMListViewItems, ListNode>(responseData, 'list');

				fireOperationalAnalytics(createAnalyticsEvent({}), 'listViewItemsQuery succeeded', {
					issuesCount: listData?.edges?.length,
					...(expVal('jira_unread_comments_in_list_view', 'isEnabled', false)
						? {
								numberOfIssuesWithUnreadComments: listData?.edges?.filter(
									(item) => item?.node?.commentSummary?.hasUnreadComment,
								).length,
							}
						: {}),
				});
				markViewMetric('list', REQUEST_END);
				if (onComplete) onComplete(responseData);
			},
			onError: (apolloError) => {
				if (!shouldHandleTheError(apolloError)) {
					return;
				}

				fireErrorAnalytics({
					meta: {
						id: 'listViewItemsQuery',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: {
						message: 'Failed to fetch issues in list view',
						...getGraphqlErrorAttributes(apolloError),
					},
					error: apolloError,
					sendToPrivacyUnsafeSplunk: true,
				});
				markViewMetric('list', REQUEST_END);
			},
			onPaginationError: (apolloError) => {
				if (!shouldHandleTheError(apolloError)) {
					return;
				}

				fireErrorAnalytics({
					meta: {
						id: 'listViewPaginationQuery',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: {
						message: 'Failed to fetch more issues in list view',
						...getGraphqlErrorAttributes(apolloError),
					},
					error: apolloError,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
			onNextPageLoaded,
		});

	useEffect(() => {
		// only call once when condition === true
		if (!data && condition) {
			markViewMetric('list', REQUEST_START);
			loadFirstPage();
			setHasLoadingStarted(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [condition, data]);

	const { sort } = useSorter<ListViewIssue>();
	const _data = getAGGCompliantData(data, 'list');
	const edges = _data?.edges || emptyArray;
	const intl = useIntl();

	const result: ListViewIssue[] = useMemo(() => {
		if (Array.isArray(edges) && edges.length === 0) {
			return edges;
		}

		const cacheVariables = {
			jql: queryVariables.jql,
			withConfluenceLinks: queryVariables.withConfluenceLinks,
			includeDoneItems: queryVariables.includeDoneItems,
			isParentFieldVisible: queryVariables.isParentFieldVisible,
			isDevSummaryFieldVisible: queryVariables.isDevSummaryFieldVisible,
			includeIsVerifiedInTeamFragment: queryVariables.includeIsVerifiedInTeamFragment,
		};

		return sort(
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			transformResult(edges as ListEdges[], {
				cacheVariables,
				intl,
			}),
		);
	}, [
		edges,
		sort,
		queryVariables.jql,
		queryVariables.withConfluenceLinks,
		queryVariables.includeDoneItems,
		queryVariables.isParentFieldVisible,
		queryVariables.isDevSummaryFieldVisible,
		queryVariables.includeIsVerifiedInTeamFragment,
		intl,
	]);

	return {
		data: result,
		// Make sure to return loading=true until items are loaded.
		loading: hasLoadingStarted ? loading : true,
		// return undefined to avoid showing the error flag in UI
		error: !shouldHandleTheError(error) ? undefined : error,
		loadNextPage,
		hasNextPage,
		isLoadingNextPage,
	};
};
